import { ChangeEvent, Component, PropsWithChildren, ReactElement, cloneElement } from "react";

import { notEmpty } from "../validation.js";

interface Error {
  error?: string;
  help?: string;
}

type Props = {
  validator?: (
    arg1?: any,
    arg2?: any
  ) => { status: string; message?: string | string[]; ok: boolean };
  req?: string | boolean;
  status?: string;
  message?: string;
  onInput: (e: ChangeEvent<any>, t?: string) => void;
  dirty?: boolean;
  value: any;
  [rest: string]: any;
};

export default function withValidation(Wrapped: any) {
  return class ValidationInput extends Component<PropsWithChildren<Props>> {
    constructor(props: Props) {
      super(props);
      this.handleChange = this.handleChange.bind(this);
      this.getValidationState = this.getValidationState.bind(this);
      this.state = { error: null, help: null, ok: null };
    }

    componentDidMount() {
      if (this.props.dirty) this.getValidationState(this.props.value);
    }

    componentDidUpdate(
      prevProps: Readonly<PropsWithChildren<Props>>,
      prevState: Readonly<{}>
    ): void {
      if (this.props.dirty && prevProps.value !== this.props.value)
        this.getValidationState(this.props.value);
    }

    getValidationState(v: string | number) {
      const { req } = this.props;
      const { status, message, ok, ...rest } = notEmpty(v);

      if (req && !ok) {
        // validate field is non-empty if it is required
        this.setState({ error: status, help: message, ok, ...rest });
      } else if (this.props.validator) {
        // then if we also have a validator, run that
        const { validator } = this.props;
        const { status, message, ...rest } = validator(v);
        this.setState({ error: status, help: message, ...rest });
      } else {
        this.setState({ error: "success", help: null, ok: true });
      }
    }

    setError(message: string) {
      this.setState({ error: "error", help: message, ok: false });
    }

    handleChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
      const v: string | number = e.target.value;
      this.props.onInput(e);
      this.getValidationState(v);
      return false;
    }

    render() {
      const { validator, children, req, ...rest } = this.props;
      const { error, help }: Error = this.state;

      return cloneElement(
        <Wrapped
          {...rest}
          help={help}
          error={error}
          handleChange={this.handleChange}
          getVState={this.getValidationState}
          req
        />,
        children as ReactElement<any>
      );
    }
  };
}
